// 몹 추적 흐름장(순수 로직, DOM·three 없음): 영웅 칸에서 몹이 설 수 있는 칸으로 넓이 우선 거리(8방향, 모서리 끼어 지나가기 금지)를 한 번 퍼뜨리고,
// 모든 몹이 같은 장을 내려가며 벽·기둥·구멍을 돌아 영웅에게 온다(몹마다 길찾기를 하지 않는다 — 몹 수와 상관없이 장 계산은 한 번).
// 장은 영웅 칸이 바뀌었을 때(최소 REBUILD_MIN_TICKS 간격)와 문 열림이 바뀔 수 있게 REBUILD_MAX_TICKS마다 다시 만든다.
// 배열은 격자 크기가 바뀔 때(지도 전환)만 새로 만들고, 프레임 루프 안에서는 할당하지 않는다.
import { Cell, type MapGrid } from './levels';

/** 장을 퍼뜨리는 최대 걸음(칸). 이보다 먼 몹은 직선 추적으로 돌아간다(닫힌 방 안의 몹은 이 안에 든다) */
export const CHASE_FIELD_MAX_STEPS = 96;
/** 영웅 칸이 바뀌어도 이 틱 안에는 다시 만들지 않는다(60Hz 기준 0.05초) */
export const CHASE_FIELD_REBUILD_MIN_TICKS = 3;
/** 영웅이 서 있어도 이 틱마다 다시 만든다(문 열림·닫힘 반영) */
export const CHASE_FIELD_REBUILD_MAX_TICKS = 30;
/** 내려갈 때 미리 보는 칸 수: 이만큼 앞 칸의 가운데를 겨누어 격자 계단 없이 부드럽게 돈다 */
const LOOKAHEAD = 3;
const UNREACHED = 0xffff;
// 8방향(앞 4개 직교, 뒤 4개 대각)
const DX = [1, -1, 0, 0, 1, -1, 1, -1];
const DZ = [0, 0, 1, -1, 1, 1, -1, -1];

export class ChaseField {
  /** 칸별 영웅까지 걸음 수(UNREACHED = 닿지 않음) */
  dist = new Uint16Array(0);
  private walk = new Uint8Array(0);
  private queue = new Int32Array(0);
  private grid: MapGrid | null = null;
  private heroCell = -1;
  private builtAt = -1e9;
  /** 마지막으로 만든 장의 정보(테스트·계측용) */
  builds = 0;
  reached = 0;

  /** 필요하면 장을 다시 만든다. gateOpen = map.gates 순서의 열림 정도(0.99 이상이 열림) */
  refresh(grid: MapGrid, gateOpen: ArrayLike<number>, hx: number, hz: number, tick: number): void {
    const cx = Math.floor(hx - grid.originX), cz = Math.floor(hz - grid.originZ);
    const inside = cx >= 0 && cz >= 0 && cx < grid.width && cz < grid.height;
    const cell = inside ? cz * grid.width + cx : -1;
    const since = tick - this.builtAt;
    if (grid === this.grid && since >= 0 && since < CHASE_FIELD_REBUILD_MAX_TICKS && (cell === this.heroCell || since < CHASE_FIELD_REBUILD_MIN_TICKS)) return;
    this.build(grid, gateOpen, cell);
    this.builtAt = tick;
  }

  /** 영웅 칸 cell에서 장을 퍼뜨린다(cell < 0 이면 빈 장) */
  build(grid: MapGrid, gateOpen: ArrayLike<number>, cell: number): void {
    const n = grid.width * grid.height;
    if (this.dist.length !== n) { this.dist = new Uint16Array(n); this.walk = new Uint8Array(n); this.queue = new Int32Array(n); }
    const dist = this.dist, walk = this.walk, queue = this.queue, W = grid.width, H = grid.height;
    // 몹이 설 수 있는 칸: 바닥이고 용암이 아니고 닫힌 문이 아니다(GameSim.walkablePoint mob=true와 같은 판정을 칸 단위로)
    for (let i = 0; i < n; i++) {
      let ok = grid.cells[i] === Cell.Floor && grid.lavaCells[i] !== 1;
      const g = grid.gateCells[i];
      if (ok && g > 0 && !((gateOpen[g - 1] ?? 0) >= 0.99)) ok = false;
      walk[i] = ok ? 1 : 0;
    }
    dist.fill(UNREACHED);
    this.grid = grid;
    this.heroCell = cell;
    this.builds++;
    this.reached = 0;
    if (cell < 0) return;
    // 영웅이 몹이 못 서는 칸(용암 등)에 있어도 그 칸에서 퍼뜨린다
    dist[cell] = 0; queue[0] = cell;
    let tail = 1;
    for (let head = 0; head < tail; head++) {
      const c = queue[head], d = dist[c];
      if (d >= CHASE_FIELD_MAX_STEPS) continue;
      const x = c % W, z = (c - x) / W;
      for (let k = 0; k < 8; k++) {
        const nx = x + DX[k], nz = z + DZ[k];
        if (nx < 0 || nz < 0 || nx >= W || nz >= H) continue;
        const nc = nz * W + nx;
        if (dist[nc] !== UNREACHED || walk[nc] === 0) continue;
        // 대각은 양옆 직교 칸이 모두 설 수 있어야 한다(기둥 모서리를 끼어 지나가지 않게)
        if (k >= 4 && (walk[z * W + nx] === 0 || walk[nz * W + x] === 0)) continue;
        dist[nc] = d + 1; queue[tail++] = nc;
      }
    }
    this.reached = tail;
  }

  /** (x,z) 칸의 영웅까지 걸음 수. 닿지 않으면 -1 */
  stepsAt(x: number, z: number): number {
    const g = this.grid;
    if (!g) return -1;
    const cx = Math.floor(x - g.originX), cz = Math.floor(z - g.originZ);
    if (cx < 0 || cz < 0 || cx >= g.width || cz >= g.height) return -1;
    const d = this.dist[cz * g.width + cx];
    return d === UNREACHED ? -1 : d;
  }

  /**
   * (x,z)의 몹이 영웅(hx,hz)에게 가려면 겨눌 점을 out에 쓴다: 장을 LOOKAHEAD칸 내려간 칸의 가운데.
   * 같은 걸음 수의 이웃이 여럿이면 영웅 쪽 방향과 가장 잘 맞는 칸을 고른다(빈 바닥에서는 거의 직선이 된다).
   * 장 밖·닿지 않는 칸·영웅 칸이면 false(호출자는 직선 추적).
   */
  steer(x: number, z: number, hx: number, hz: number, out: { x: number; z: number }): boolean {
    const g = this.grid;
    if (!g) return false;
    const W = g.width, H = g.height;
    let cx = Math.floor(x - g.originX), cz = Math.floor(z - g.originZ);
    if (cx < 0 || cz < 0 || cx >= W || cz >= H) return false;
    let d = this.dist[cz * W + cx];
    if (d === UNREACHED || d === 0) return false;
    const walk = this.walk, dist = this.dist;
    const hcx = hx - g.originX - 0.5, hcz = hz - g.originZ - 0.5;
    for (let s = 0; s < LOOKAHEAD && d > 0; s++) {
      let best = -1, bestDot = -Infinity;
      const tx = hcx - cx, tz = hcz - cz, tl = Math.hypot(tx, tz) || 1;
      for (let k = 0; k < 8; k++) {
        const nx = cx + DX[k], nz = cz + DZ[k];
        if (nx < 0 || nz < 0 || nx >= W || nz >= H) continue;
        const nc = nz * W + nx;
        if (dist[nc] !== d - 1) continue;
        if (k >= 4 && (walk[cz * W + nx] === 0 || walk[nz * W + cx] === 0)) continue;
        const dot = (DX[k] * tx + DZ[k] * tz) / (tl * (k >= 4 ? Math.SQRT2 : 1));
        if (dot > bestDot) { bestDot = dot; best = k; }
      }
      if (best < 0) break;
      cx += DX[best]; cz += DZ[best]; d--;
    }
    out.x = g.originX + cx + 0.5; out.z = g.originZ + cz + 0.5;
    return true;
  }
}
